import React, { useMemo } from 'react';
import NewsAvatar from '@/components/NewsAvatar';
import { displayInitials, formatElapsed } from '@/utils/groupGameBareme';

const COLUMN_SIZE = 20;
const COLUMNS_PER_BAND = 4;

function Face({ row, size = 56 }) {
  if (row?.photo) {
    return <NewsAvatar src={row.photo} size={size} alt={row.display_name || ''} />;
  }
  return (
    <div
      className="rounded-full bg-[#f76f4b] text-white font-bold flex items-center justify-center shrink-0"
      style={{ width: size, height: size, fontSize: size > 40 ? 16 : 12 }}
    >
      {displayInitials(row?.display_name)}
    </div>
  );
}

function splitRankedAndOthers(rows = []) {
  const ranked = [];
  const others = [];
  for (const row of rows) {
    if (row?.rank != null) ranked.push(row);
    else others.push(row);
  }
  return { ranked, others };
}

function chunkIntoColumns(items, size = COLUMN_SIZE) {
  const columns = [];
  for (let i = 0; i < items.length; i += size) {
    columns.push(items.slice(i, i + size));
  }
  return columns;
}

function bandColumns(columns, perBand = COLUMNS_PER_BAND) {
  const bands = [];
  for (let i = 0; i < columns.length; i += perBand) {
    bands.push(columns.slice(i, i + perBand));
  }
  return bands;
}

export default function Podium({ rows = [], compact = false, showElapsed = false, showMoves = false }) {
  const { ranked } = splitRankedAndOthers(rows);
  const top = [ranked[1], ranked[0], ranked[2]];
  const heights = compact ? ['h-12', 'h-16', 'h-10'] : ['h-16', 'h-24', 'h-12'];
  const ranks = [2, 1, 3];
  if (!ranked.length) {
    return null;
  }
  return (
    <div className={`flex items-end justify-center ${compact ? 'gap-2 py-2' : 'gap-4 py-4'}`}>
      {top.map((row, i) => (
        <div key={ranks[i]} className={`flex flex-col items-center ${compact ? 'w-20' : 'w-24'}`}>
          {row ? (
            <>
              <Face row={row} size={compact ? 40 : 56} />
              <p className="mt-1 text-xs font-semibold text-center text-gray-800 dark:text-gray-100 line-clamp-2">
                {row.display_name}
              </p>
              {showMoves ? (
                <p className="text-[10px] text-gray-400">{row.moves ?? 0} coup{(row.moves ?? 0) !== 1 ? 's' : ''}</p>
              ) : null}
              {showElapsed ? (
                <p className="text-[10px] font-mono text-gray-400">{formatElapsed(row.elapsed_ms)}</p>
              ) : null}
              <p className="text-[10px] text-gray-400">{row.points || 0} pts</p>
            </>
          ) : (
            <div className={compact ? 'h-10' : 'h-14'} />
          )}
          <div className={`w-full mt-2 rounded-t-md bg-[#f76f4b]/80 text-white text-sm font-bold flex items-center justify-center ${heights[i]}`}>
            {ranks[i]}
          </div>
        </div>
      ))}
    </div>
  );
}

function RowMetrics({ row, showMoves, showElapsed, showPoints }) {
  if (row.rank == null && row.played === false) {
    return <span className="text-gray-400 shrink-0 text-[10px]">non joué</span>;
  }
  if (row.rank == null && row.played !== false) {
    return (
      <span className="shrink-0 text-[10px] text-gray-400 text-right leading-tight">
        <span className="block">en cours</span>
        {showElapsed && row.elapsed_ms > 0 ? (
          <span className="block font-mono">{formatElapsed(row.elapsed_ms)}</span>
        ) : null}
      </span>
    );
  }
  return (
    <span className="shrink-0 text-[10px] text-gray-500 dark:text-gray-400 text-right leading-tight">
      {showMoves ? <span className="block">{row.moves ?? 0} coup{(row.moves ?? 0) !== 1 ? 's' : ''}</span> : null}
      {showElapsed ? <span className="block font-mono">{formatElapsed(row.elapsed_ms)}</span> : null}
      {showPoints ? <span className="block">{row.points || 0} pts</span> : null}
    </span>
  );
}

export function RankingList({
  rows = [],
  meId,
  skip = 3,
  showMoves = false,
  showElapsed = false,
  showPoints = true,
}) {
  const bands = useMemo(() => {
    const { ranked, others } = splitRankedAndOthers(rows);
    const afterPodium = ranked.slice(Math.min(skip, ranked.length));
    const visible = [...afterPodium, ...others];
    return bandColumns(chunkIntoColumns(visible, COLUMN_SIZE), COLUMNS_PER_BAND);
  }, [rows, skip]);

  if (!rows.length) {
    return <p className="text-xs text-gray-400">Personne n’a encore validé.</p>;
  }
  if (!bands.length) return null;

  return (
    <div className="mt-3 space-y-6">
      {bands.map((columns, bandIndex) => (
        <div
          key={`band-${bandIndex}`}
          className="flex flex-wrap justify-center gap-x-4 gap-y-2"
        >
          {columns.map((column, colIndex) => (
            <ul
              key={`col-${bandIndex}-${colIndex}`}
              className="w-[13.5rem] sm:w-[14.5rem] space-y-1 shrink-0"
            >
              {column.map((row) => (
                <li
                  key={row.user_id}
                  className={`flex items-center gap-1.5 text-xs rounded px-1.5 py-1 ${
                    row.user_id === meId ? 'bg-orange-50 dark:bg-orange-950/30' : ''
                  }`}
                >
                  <span className="w-5 font-bold text-gray-500 tabular-nums shrink-0">
                    {row.rank ?? '—'}
                  </span>
                  <Face row={row} size={24} />
                  <span className="flex-1 min-w-0 truncate text-gray-800 dark:text-gray-100">
                    {row.display_name}
                  </span>
                  <RowMetrics
                    row={row}
                    showMoves={showMoves}
                    showElapsed={showElapsed}
                    showPoints={showPoints}
                  />
                </li>
              ))}
            </ul>
          ))}
        </div>
      ))}
    </div>
  );
}
